import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { useForm, Controller } from "react-hook-form";
import {
  useAddPatientAlertMutation,
  useEditPatientAlertMutation,
} from "services/patient-alerts";
import { PatientAlert } from "services/types/patient-alerts.type";
import { format } from "date-fns";
import {
  setCustomMessage,
  setOpen,
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import DateFormField from "components/date-form-field";
import * as React from "react";
import { useEffect } from "react";
import { useAppDispatch } from "app/hooks";
import usePatientAlertPopupMode from "features/patients/patient-alerts-review/use-patient-alert-popup-mode.hook";
import { formatBackendDateOnly } from "utils/common";

interface AlertDialogProps {
  open: boolean;
  onClose: () => void;
  patientId?: number;
  patientName?: string;
  refetch: () => void;
  // When set, the dialog edits this alert instead of creating a new one.
  alert?: PatientAlert;
}

interface FormData {
  alert_date: string;
  description: string;
}

export default function AlertDialog({
  open,
  onClose,
  patientId,
  patientName,
  refetch,
  alert,
}: AlertDialogProps) {
  const dispatch = useAppDispatch();
  const { isOn: isReviewOn } = usePatientAlertPopupMode();
  // Hosts clear their editingAlert state the moment the dialog closes, but the
  // MUI fade-out is still showing — keep the last-seeded alert so the title
  // doesn't flip to "New Alert" mid-transition.
  const [displayAlert, setDisplayAlert] = React.useState<
    PatientAlert | undefined
  >(undefined);
  const isEdit = Boolean(open ? alert : displayAlert);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FormData>({
    defaultValues: {
      alert_date: "",
      description: "",
    },
    mode: "onChange",
  });

  // The dialog stays mounted between opens, so seed the form each time it
  // opens — from the alert in edit mode, blank in create mode.
  useEffect(() => {
    if (!open) return;
    setDisplayAlert(alert);
    reset({
      alert_date: alert?.alert_date
        ? formatBackendDateOnly(alert.alert_date, "MM/dd/yyyy")
        : "",
      description: alert?.alert_description ?? "",
    });
  }, [open, alert, reset]);

  const [addAlert, { isLoading: isAdding }] = useAddPatientAlertMutation();
  const [editAlert, { isLoading: isEditing }] = useEditPatientAlertMutation();
  const isLoading = isAdding || isEditing;

  const onSubmit = async (data: FormData) => {
    if (!alert && !patientId) return;

    const request = alert
      ? // A cleared field must PATCH null — omitting the key would leave the
        // stored value in place, so "remove the date" would silently not work.
        editAlert({
          id: alert.id,
          alert_date: data.alert_date
            ? format(new Date(data.alert_date), "yyyy-MM-dd")
            : null,
          alert_description: data.description || null,
        })
      : addAlert({
          patient: patientId as number,
          alert_date: data.alert_date
            ? format(new Date(data.alert_date), "yyyy-MM-dd")
            : undefined,
          alert_description: data.description || undefined,
        });

    await request.then((response) => {
      if ("error" in response) {
        dispatch(
          setCustomMessage(<ErrorSnackbarContent response={response} />)
        );
        dispatch(setOpen(true));
      } else {
        dispatch(setCustomMessage(isEdit ? "Alert was updated" : "Alert was created"));
        dispatch(setOpen(true));
        refetch();
        handleClose();
      }
    });
  };

  // No reset here — the open-time effect reseeds the form, and resetting now
  // would blank the fields while the dialog is still fading out.
  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit Alert" : "New Alert"}
        {patientName ? ` for ${patientName}` : ""}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <DateFormField
              name="alert_date"
              label="Alert Date"
              control={control}
              sx={{}}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Description"
                  multiline
                  rows={4}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter alert description..."
                />
              )}
            />
            {isEdit && isReviewOn && (
              // DEV-3110: an edit bumps the alert's updated_at, which invalidates every
              // acknowledgement — say so before the person saves.
              <Alert severity="warning" data-testid="alert-edit-resets-reviews">
                <strong>Changing this alert resets its reviews.</strong> Everyone else will
                be asked to review it again the next time they open this patient.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || isLoading}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
